import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Mail, MessageSquare, HelpCircle, 
  AlertCircle, CreditCard, Send
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmergencyCardContact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.message) {
      toast.error('Please fill in email and message');
      return;
    }
    
    setSending(true);
    
    try {
      await axios.post(`${API_URL}/contact`, {
        email: formData.email,
        subject: formData.subject || 'General Inquiry',
        message: formData.message,
        app_source: 'My EmergencyCard App'
      });
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const quickTopics = [
    { icon: CreditCard, label: 'Payment Issue', subject: 'Payment Issue' },
    { icon: HelpCircle, label: 'How to Use', subject: 'Help: How to Use Emergency Card' },
    { icon: AlertCircle, label: 'Report a Problem', subject: 'Bug Report' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-[#D92B2B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              const basePath = window.location.pathname.includes('emergency-app') ? '/emergency-app' : '/';
              navigate(basePath);
            }}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Contact Us</h1>
            <p className="text-xs text-[#FECACA]">Emergency Card Support</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 pb-12">
        {/* Email Card */}
        <Card className="mb-6 border-[#FECACA]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#FEE2E2] rounded-full">
                <Mail className="h-6 w-6 text-[#D92B2B]" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Email us directly at</p>
                <a 
                  href="mailto:support@myemergencycard.app" 
                  className="text-lg font-bold text-[#D92B2B] hover:underline"
                >
                  support@myemergencycard.app
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Topics */}
        <div className="mb-6">
          <p className="text-sm text-slate-600 mb-3">Quick topics:</p>
          <div className="flex flex-wrap gap-2">
            {quickTopics.map((topic) => (
              <Button
                key={topic.label}
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setFormData({ ...formData, subject: topic.subject })}
              >
                <topic.icon className="h-4 w-4" />
                {topic.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="h-5 w-5 text-[#D92B2B]" />
              <h2 className="font-bold text-slate-800">Send us a Message</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Your Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="mt-1"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="What's this about?"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us how we can help..."
                  rows={4}
                  className="mt-1"
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[#D92B2B] hover:bg-[#C02424]"
                disabled={sending}
              >
                {sending ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <div className="mt-8">
          <h2 className="font-bold text-slate-800 mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800">Can I get a refund?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Due to instant delivery, refunds are limited. Contact us if you have issues.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800">I lost my QR code. Can you recover it?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  No - we don't store your data. You'll need to create a new card. 
                  We recommend backing up your QR code image.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800">How do I update my information?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Create a new Emergency Card with updated information and save the new QR code.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-800">Is my data secure?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Your data is encoded directly in your QR code - we never store it on our servers. 
                  This means anyone who scans your QR can read your medical info, which is intentional 
                  for emergency situations. Keep your QR code safe like any medical ID.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmergencyCardContact;
